import api from './axiosConfig';
import {
  CreateLoanRequestPayload,
  LoanDetailDto,
  LoanEmployeeOptionDto,
  LoanInterestMethod,
  LoanListItemDto,
  LoanSchedulePreviewDto,
  LoanStatus,
  LoanSummaryDto,
  LoanType,
} from '../types/loanTypes';

export interface LoanFilters {
  status?: LoanStatus;
  loanType?: LoanType;
  employeeId?: string;
  departmentId?: string;
  search?: string;
}

export interface SchedulePreviewPayload {
  principalAmount: number;
  interestMethod: LoanInterestMethod;
  interestRatePercent: number;
  totalInstallments: number;
  repaymentStartMonth: number;
  repaymentStartYear: number;
}

export const loanApi = {
  getLoans: async (filters: LoanFilters = {}): Promise<LoanListItemDto[]> => {
    const res = await api.get<LoanListItemDto[]>('/loans', { params: filters });
    return res.data;
  },

  getLoan: async (id: string): Promise<LoanDetailDto> => {
    const res = await api.get<LoanDetailDto>(`/loans/${id}`);
    return res.data;
  },

  getSummary: async (): Promise<LoanSummaryDto> => {
    const res = await api.get<LoanSummaryDto>('/loans/summary');
    return res.data;
  },

  getEmployeeOptions: async (): Promise<LoanEmployeeOptionDto[]> => {
    const res = await api.get<LoanEmployeeOptionDto[]>('/loans/employee-options');
    return res.data;
  },

  previewSchedule: async (payload: SchedulePreviewPayload): Promise<LoanSchedulePreviewDto> => {
    const res = await api.post<LoanSchedulePreviewDto>('/loans/schedule-preview', payload);
    return res.data;
  },

  createLoan: async (payload: CreateLoanRequestPayload): Promise<{ id: string }> => {
    const res = await api.post<{ id: string }>('/loans', payload);
    return res.data;
  },

  updateLoan: async (id: string, payload: Omit<SchedulePreviewPayload, never> & { purpose?: string }): Promise<void> => {
    await api.put(`/loans/${id}`, payload);
  },

  approveLoan: async (id: string, remarks?: string): Promise<void> => {
    await api.post(`/loans/${id}/approve`, { remarks });
  },

  rejectLoan: async (id: string, remarks?: string): Promise<void> => {
    await api.post(`/loans/${id}/reject`, { remarks });
  },

  cancelLoan: async (id: string, remarks?: string): Promise<void> => {
    await api.post(`/loans/${id}/cancel`, { remarks });
  },

  disburseLoan: async (
    id: string,
    payload: { disbursedAmount?: number; disbursedOn?: string; reference?: string },
  ): Promise<void> => {
    await api.post(`/loans/${id}/disburse`, payload);
  },

  recordRepayment: async (
    id: string,
    payload: { amount: number; paidOn?: string; reference?: string; remarks?: string },
  ): Promise<void> => {
    await api.post(`/loans/${id}/repayments`, payload);
  },

  forecloseLoan: async (
    id: string,
    payload: { paidOn?: string; reference?: string; remarks?: string },
  ): Promise<void> => {
    await api.post(`/loans/${id}/foreclose`, payload);
  },

  writeOffLoan: async (id: string, remarks?: string): Promise<void> => {
    await api.post(`/loans/${id}/write-off`, { remarks });
  },

  skipInstallment: async (id: string, installmentId: string, remarks?: string): Promise<void> => {
    await api.post(`/loans/${id}/installments/${installmentId}/skip`, { remarks });
  },

  waiveInstallment: async (id: string, installmentId: string, remarks?: string): Promise<void> => {
    await api.post(`/loans/${id}/installments/${installmentId}/waive`, { remarks });
  },

  deleteLoan: async (id: string): Promise<void> => {
    await api.delete(`/loans/${id}`);
  },

  // ── Employee self-service ──
  getMyLoans: async (): Promise<LoanListItemDto[]> => {
    const res = await api.get<LoanListItemDto[]>('/loans/my');
    return res.data;
  },

  getMyLoan: async (id: string): Promise<LoanDetailDto> => {
    const res = await api.get<LoanDetailDto>(`/loans/my/${id}`);
    return res.data;
  },

  requestLoan: async (payload: CreateLoanRequestPayload): Promise<{ id: string }> => {
    const res = await api.post<{ id: string }>('/loans/my', payload);
    return res.data;
  },
};
